<script setup lang="ts">
import { ref } from 'vue'
import { healthService } from '@/api/health'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faBiohazard,
  faCalendarDay,
  faCalendarCheck,
  faNotesMedical,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faShieldHalved,
  faFileContract
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faBiohazard,
  faCalendarDay,
  faCalendarCheck,
  faNotesMedical,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faShieldHalved,
  faFileContract
)

interface Animal {
  id: string
  species: string
}

const props = defineProps<{
  animal: Animal
}>()

const emit = defineEmits<{
  success: []
}>()

const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const formData = ref({
  reason: '',
  start_date: new Date().toISOString().split('T')[0],
  expected_end_date: '',
  release_notes: ''
})

async function submitForm() {
  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    await healthService.createQuarantine({
      ...formData.value,
      animal: props.animal.id,
      released: false
    })
    
    successMessage.value = 'Isolation protocol initiated successfully!'
    setTimeout(() => {
      emit('success')
    }, 1500)
  } catch (err: any) {
    errorMessage.value = err.response?.data?.detail || err.message || 'Failed to initiate isolation'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <form @submit.prevent="submitForm" class="space-y-6">
    <!-- Isolation Rationale Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-amber-500 rounded-full"></div>
          Isolation Rationale
        </h3>
      </div>
      
      <div class="p-8 space-y-8">
        <!-- Reason -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Primary Reason <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute top-4 left-4 pointer-events-none text-slate-300 group-focus-within:text-amber-500 transition-colors">
              <FontAwesomeIcon icon="biohazard" />
            </div>
            <textarea 
              v-model="formData.reason" 
              rows="3" 
              required 
              placeholder="Describe why this animal requires isolation (e.g. suspected Foot and Mouth, new stock intake)..." 
              class="block w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 text-slate-700 font-bold transition-all"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Timeline & Protocols Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-blue-500 rounded-full"></div>
          Biosafety Timeline
        </h3>
      </div>

      <div class="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Start Date -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Commencement <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="calendar-day" />
            </div>
            <input 
              v-model="formData.start_date" 
              type="date" 
              required 
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Expected End Date -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Scheduled Release <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="calendar-check" />
            </div>
            <input 
              v-model="formData.expected_end_date" 
              type="date" 
              required 
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Documentation Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-slate-400 rounded-full"></div>
          Administrative Documentation
        </h3>
      </div>
      <div class="p-8 text-sm">
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Quarantine Field Notes</label>
          <div class="relative group">
            <div class="absolute top-4 left-4 pointer-events-none text-slate-300 group-focus-within:text-slate-600 transition-colors">
              <FontAwesomeIcon icon="file-contract" />
            </div>
            <textarea 
              v-model="formData.release_notes" 
              rows="3" 
              placeholder="Initial observations, specific isolation unit ID, or additional biosafety requirements..." 
              class="block w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-slate-400 focus:ring-4 focus:ring-slate-400/10 text-slate-700 font-bold transition-all"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Alert Messages -->
    <div class="space-y-3">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="successMessage" class="bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="check-circle" class="mr-3 text-lg" />
          {{ successMessage }}
        </div>
      </transition>
      
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="errorMessage" class="bg-rose-50 border border-rose-100 text-rose-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="circle-exclamation" class="mr-3 text-lg" />
          {{ errorMessage }}
        </div>
      </transition>
    </div>

    <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
      <button 
        type="button" 
        @click="$emit('success')" 
        class="px-8 py-3.5 text-slate-400 font-black rounded-2xl hover:bg-slate-50 hover:text-slate-600 transition-all active:scale-95 flex items-center gap-2"
      >
        <FontAwesomeIcon icon="times" />
        Discard
      </button>
      <button 
        type="submit" 
        :disabled="loading"
        class="px-10 py-3.5 bg-slate-900 text-white font-black rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-100 active:scale-95 disabled:bg-slate-200 disabled:shadow-none flex items-center gap-3"
      >
        <FontAwesomeIcon v-if="loading" icon="circle-notch" class="animate-spin" />
        <FontAwesomeIcon v-else icon="save" />
        {{ loading ? 'Securing Protocol...' : 'Authorize Isolation' }}
      </button>
    </div>
  </form>
</template>
